import { formaterAMadagascar } from '@afs/shared'
/**
 * Ce que le centre de notifications DÉDUIT — hors du composant, exprès.
 *
 * Un rendu qui se trompe se voit à l'écran. Ces décisions-là, non : un badge
 * qui affiche « 247 » et déforme le menu, une date qui dit « il y a 0 minute »
 * pendant une heure, une reconnexion qui martèle le serveur après une
 * déconnexion volontaire. Elles pourrissent en silence, donc elles se testent.
 */

/**
 * Au-delà, le badge affiche « 9+ ».
 *
 * Ce n'est pas de la place gagnée : passé une poignée, le nombre exact ne
 * change plus rien à ce qu'on fait — on ouvre le panneau. Et un badge à trois
 * chiffres déforme la cloche.
 */
export const PLAFOND_BADGE = 9

/** Le texte du badge, ou `null` quand il n'y a rien à signaler. */
export function badgeNonLues(nonLues: number): string | null {
  if (nonLues <= 0) return null
  return nonLues > PLAFOND_BADGE ? `${PLAFOND_BADGE}+` : String(nonLues)
}

/**
 * Ce qu'un lecteur d'écran annonce sur la cloche.
 *
 * Le badge est un chiffre sans contexte : seul, il se lit « neuf plus », ce
 * qui ne dit ni de quoi ni pourquoi.
 */
export function libelleCloche(nonLues: number): string {
  if (nonLues <= 0) return 'Notifications — aucune non lue'
  if (nonLues === 1) return 'Notifications — 1 non lue'
  return `Notifications — ${String(nonLues)} non lues`
}

const MINUTE = 60_000
const HEURE = 60 * MINUTE
const JOUR = 24 * HEURE

/**
 * L'ancienneté, en français, telle qu'on la dirait.
 *
 * L'horloge est un paramètre : une fonction qui lit `Date.now()` toute seule
 * ne se teste qu'en déplaçant l'heure de la machine.
 *
 * Au-delà d'une semaine, la date absolue redevient plus utile que l'écart —
 * « il y a 23 jours » oblige à compter.
 */
export function ilYA(instantIso: string, maintenant: Date): string {
  const instant = new Date(instantIso)
  const ecart = maintenant.getTime() - instant.getTime()

  // Une horloge de poste en avance sur le serveur produirait « dans 2
  // minutes » sur une alerte qui vient d'arriver.
  if (ecart < MINUTE) return "à l'instant"

  if (ecart < HEURE) {
    const minutes = Math.floor(ecart / MINUTE)
    return `il y a ${String(minutes)} min`
  }

  if (ecart < JOUR) {
    const heures = Math.floor(ecart / HEURE)
    return heures === 1 ? 'il y a 1 heure' : `il y a ${String(heures)} heures`
  }

  const jours = Math.floor(ecart / JOUR)
  if (jours === 1) return 'hier'
  if (jours < 7) return `il y a ${String(jours)} jours`

  return formaterAMadagascar(instant, { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * La fermeture applicative que l'API pose quand la session n'existe plus.
 *
 * Doit rester alignée sur `FERMETURE_SESSION_CLOSE` de
 * `apps/api/src/core/temps-reel/plugin.ts`. Le code de fermeture n'est pas
 * un schéma partagé : c'est un nombre du protocole WebSocket, hors de portée
 * de Zod.
 */
export const FERMETURE_SESSION_CLOSE = 4001

/**
 * Faut-il retenter après cette fermeture ?
 *
 * Non quand le serveur a dit que la session est close : reconnecter en boucle
 * ne ferait que marteler l'API avec un jeton mort. Toute autre fermeture est
 * un incident réseau — Wi-Fi perdu, coupure, redéploiement — et mérite une
 * nouvelle tentative.
 */
export function doitSeReconnecter(code: number): boolean {
  return code !== FERMETURE_SESSION_CLOSE
}

/** Le délai avant la première nouvelle tentative. */
export const DELAI_RECONNEXION_MS = 1000

/** Au-delà, inutile d'attendre davantage : le réseau reviendra ou non. */
export const DELAI_RECONNEXION_MAX_MS = 30_000

/**
 * Le délai d'attente avant la n-ième tentative, en millisecondes.
 *
 * Doublement plafonné : une coupure de réseau touche les onze sites en même
 * temps, et onze postes qui retentent chaque seconde feraient de la reprise
 * de l'API une seconde panne.
 */
export function delaiDeReconnexion(tentative: number): number {
  const brut = DELAI_RECONNEXION_MS * 2 ** Math.max(0, tentative - 1)
  return Math.min(brut, DELAI_RECONNEXION_MAX_MS)
}

/**
 * L'adresse de la connexion temps réel, déduite de celle de la page.
 *
 * Relative et non configurée : le front est servi par le même domaine que
 * l'API en production comme en local. Une variable d'environnement de plus
 * serait une variable de plus à se tromper. `chemin` : celle des personnes
 * par défaut ; l'écran d'appel de salle d'attente a la sienne.
 */
export function adresseTempsReel(origine: string, chemin = '/temps-reel'): string {
  return `${origine.replace(/^http/, 'ws')}/api/v1${chemin}`
}

/**
 * Où mène une notification : l'écran de ce dont elle parle. Une alerte qu'on
 * ne peut pas ouvrir oblige à chercher soi-même l'import, la fiche ou le
 * compte — elle ne dit plus seulement quoi faire, elle fait perdre du temps.
 */
export function lienDe(n: {
  readonly entiteType: string | null
  readonly entiteId: string | null
}): string | null {
  if (n.entiteType === null) return null
  if (n.entiteType === 'utilisateur') return '/administration/utilisateurs'
  if (n.entiteId === null) return null
  const routes: Readonly<Record<string, string>> = {
    dns_import: `/dns/${n.entiteId}`,
    personne: `/accueil/patients/${n.entiteId}`,
    adherent: `/entreprises/${n.entiteId}`,
    feuille_coupure: `/parcours/coupure/${n.entiteId}`,
    // L'écart d'offre se tranche sur sa page, à part des fiches à confirmer (G19, point 9).
    signalement_offre: '/parcours/ecarts-offre',
    // R13 — la caisse oubliée : l'écran Caisse de la Direction, à la section des oublis.
    caisse_journee: '/pilotage/caisse#non-cloturees',
    // Décision 146 : le signalement s'ouvre directement dans la liste du Super admin.
    signalement: `/administration/signalements?id=${n.entiteId}`,
    // Décision 177 : la réponse mène l'auteur à « Mes signalements », au bon signalement.
    mon_signalement: `/mes-signalements?id=${n.entiteId}`,
  }
  return routes[n.entiteType] ?? null
}

/**
 * Faut-il sonner ? Recette du 28/09 : toutes les notifications sonnent.
 *
 * Le signal temps réel part aussi quand une notification est LUE : sonner sur
 * lui ferait sonner à tort. On sonne quand une notification non lue apparaît
 * qu'on n'avait pas encore vue. `vues` est `null` au premier chargement de la
 * session : on apprend ce qui est déjà là, sans sonner — sinon chaque
 * connexion commencerait par un son pour les notifications de la veille.
 *
 * Rend le nouvel ensemble des vues, et s'il faut sonner (une fois, même si
 * plusieurs arrivent ensemble).
 */
export function aSonner(
  vues: ReadonlySet<string> | null,
  dernieres: readonly { readonly id: string; readonly lueLe: string | null }[],
): { vues: Set<string>; sonner: boolean } {
  const suivantes = new Set(vues ?? [])
  let sonner = false
  for (const n of dernieres) {
    if (suivantes.has(n.id)) continue
    suivantes.add(n.id)
    if (vues !== null && n.lueLe === null) sonner = true
  }
  return { vues: suivantes, sonner }
}
